import { DisconnectButton, useTrackToggle } from '@livekit/components-react';
import { Track } from 'livekit-client';
import {
  Mic,
  MicOff,
  Video,
  VideoOff,
  MonitorUp,
  MessageSquare,
  Users,
  Smile,
  PhoneOff,
  Presentation,
  Pencil,
  type LucideIcon,
} from 'lucide-react';

interface Props {
  onToggleParticipants: () => void;
  onToggleChat: () => void;
  onToggleWhiteboard?: () => void;
  onToggleAnnotation?: () => void;
  annotating?: boolean;
  onReact?: (emoji: string) => void;
}

const tile =
  'flex flex-col items-center gap-1 rounded-lg px-3 py-2 text-xs text-slate-200 hover:bg-slate-700 transition';

function MediaButton({
  source,
  OnIcon,
  OffIcon,
  onLabel,
  offLabel,
  captureAudio,
}: {
  source: Track.Source.Microphone | Track.Source.Camera | Track.Source.ScreenShare;
  OnIcon: LucideIcon;
  OffIcon: LucideIcon;
  onLabel: string;
  offLabel: string;
  captureAudio?: boolean;
}): JSX.Element {
  const { enabled, toggle, pending } = useTrackToggle({
    source,
    ...(captureAudio ? { captureOptions: { audio: true } } : {}),
  });
  const Icon = enabled ? OnIcon : OffIcon;
  return (
    <button className={tile} onClick={() => void toggle()} disabled={pending}>
      <Icon size={20} className={enabled ? '' : 'text-red-400'} />
      <span>{enabled ? onLabel : offLabel}</span>
    </button>
  );
}

export default function Toolbar({
  onToggleParticipants,
  onToggleChat,
  onToggleWhiteboard,
  onToggleAnnotation,
  annotating,
  onReact,
}: Props): JSX.Element {
  return (
    <div className="flex items-center justify-center gap-2 border-t border-slate-800 bg-slate-900 px-4 py-3">
      <MediaButton source={Track.Source.Microphone} OnIcon={Mic} OffIcon={MicOff} onLabel="Mute" offLabel="Unmute" />
      <MediaButton source={Track.Source.Camera} OnIcon={Video} OffIcon={VideoOff} onLabel="Stop" offLabel="Start" />
      <MediaButton
        source={Track.Source.ScreenShare}
        OnIcon={MonitorUp}
        OffIcon={MonitorUp}
        onLabel="Stop share"
        offLabel="Share"
        captureAudio
      />

      <button className={tile} onClick={() => onToggleAnnotation?.()}>
        <Pencil size={20} className={annotating ? 'text-brand-400' : ''} />
        <span>Annotate</span>
      </button>
      <button className={tile} onClick={() => onToggleWhiteboard?.()}>
        <Presentation size={20} />
        <span>Board</span>
      </button>
      <button className={tile} onClick={onToggleChat}>
        <MessageSquare size={20} />
        <span>Chat</span>
      </button>
      <button className={tile} onClick={onToggleParticipants}>
        <Users size={20} />
        <span>People</span>
      </button>
      <button className={tile} onClick={() => onReact?.('👍')}>
        <Smile size={20} />
        <span>React</span>
      </button>

      <DisconnectButton className="ml-2 flex flex-col items-center gap-1 rounded-lg bg-red-600 px-4 py-2 text-xs text-white hover:bg-red-700">
        <PhoneOff size={20} />
        <span>Leave</span>
      </DisconnectButton>
    </div>
  );
}
